/**
 * Rule catalogue and page-section index used by draw-to-add-finding.
 *
 * The catalogue mirrors the 78 rules the backend rule engine ships
 * (see `api/tools/data-synthesis/fpl_rules.py`): id, section, default
 * severity, KPI group, and citation are the rule's identity. When the
 * `useRules()` query lights up, it returns this exact shape and this
 * static export can be removed without changing the dialog contract.
 *
 * Severity is normalized from the backend's `warn` → our `warning`.
 * Sections are mapped to the on-screen `PageSection` keys where they
 * line up; rules that apply broadly (text, graphics, defensibility)
 * carry no `section` and stay pickable regardless of what region the
 * reviewer drew on.
 */
import type { RuleOption } from "../findings/add-finding-dialog"
import type { PageSection } from "../pdf/pdf-surface"

export const PAGE_SECTIONS: PageSection[] = [
  { key: "front_panel", label: "front panel", y0: 0, y1: 150 },
  { key: "ingredients", label: "ingredient statement", y0: 150, y1: 270 },
  { key: "first_aid", label: "first aid", y0: 270, y1: 340 },
  {
    key: "precautionary_statements",
    label: "precautionary statements",
    y0: 340,
    y1: 430,
  },
  { key: "directions_for_use", label: "directions for use", y0: 430, y1: 520 },
  { key: "storage_disposal", label: "storage and disposal", y0: 520, y1: 792 },
]

export const RULE_CATALOG: RuleOption[] = [
  {
    id: "FP-01",
    label: "Brand name matches EPA approval (incl. alternate/distributor names)",
    section: "front_panel",
    severity: "error",
    groupId: "g-format",
    authorities: [
      { kind: "lrm", label: "LRM Ch 15" },
      { kind: "internal", label: "PPLS" },
      { kind: "internal", label: "Final Label Review" },
    ],
  },
  {
    id: "FP-02",
    label: "Restricted Use statement at very top, boxed, nothing above it",
    section: "front_panel",
    severity: "error",
    groupId: "g-format",
    authorities: [
      { kind: "cfr", label: "40 CFR 156 Subpart A" },
      { kind: "lrm", label: "LRM Ch 6" },
      { kind: "internal", label: "Final Label Review" },
    ],
  },
  {
    id: "FP-03",
    label: "'Restricted Use Pesticide' heading same point size as signal word",
    section: "front_panel",
    severity: "error",
    groupId: "g-format",
    authorities: [
      { kind: "lrm", label: "LRM Ch 6" },
      { kind: "internal", label: "Final Label Review" },
    ],
  },
  {
    id: "FP-04",
    label: "Ingredient statement present",
    section: "front_panel",
    severity: "error",
    groupId: "g-format",
    authorities: [
      { kind: "cfr", label: "40 CFR 156 Subpart A" },
      { kind: "lrm", label: "LRM Ch 5" },
    ],
  },
  {
    id: "FP-05",
    label: "Active/Other/Total aligned by decimal point",
    section: "front_panel",
    severity: "warning",
    groupId: "g-format",
    authorities: [{ kind: "lrm", label: "LRM Ch 5" }],
  },
  {
    id: "FP-06",
    label: "Ingredients expressed in % (by w/w)",
    section: "front_panel",
    severity: "error",
    groupId: "g-format",
    authorities: [{ kind: "lrm", label: "LRM Ch 5" }],
  },
  {
    id: "FP-07",
    label: "Active & Other ingredient headings same type size, margin, prominence",
    section: "front_panel",
    severity: "warning",
    groupId: "g-format",
    authorities: [{ kind: "lrm", label: "LRM Ch 5" }],
  },
  {
    id: "FP-08",
    label: "Petroleum distillates / sodium nitrite statement below ingredient stmt if applicable",
    section: "front_panel",
    severity: "error",
    groupId: "g-format",
    authorities: [{ kind: "lrm", label: "LRM Ch 7" }],
  },
  {
    id: "FP-09",
    label: "EPA Registration Number present and correctly formatted",
    section: "front_panel",
    severity: "error",
    groupId: "g-format",
    authorities: [
      { kind: "cfr", label: "40 CFR 156 Subpart A" },
      { kind: "lrm", label: "LRM Ch 14" },
    ],
  },
  {
    id: "FP-10",
    label: "EPA Establishment Number present; multi-est designation language present",
    section: "front_panel",
    severity: "error",
    groupId: "g-format",
    authorities: [
      { kind: "cfr", label: "40 CFR Part 167" },
      { kind: "lrm", label: "LRM Ch 14" },
    ],
  },
  {
    id: "FP-11",
    label: "Company name & address with proper heading (e.g. 'Distributed by:')",
    section: "front_panel",
    severity: "warning",
    groupId: "g-format",
    authorities: [{ kind: "lrm", label: "LRM Ch 15" }],
  },
  {
    id: "FP-12",
    label: "Mode-of-Action numerical classification (upper-right)",
    section: "front_panel",
    severity: "warning",
    groupId: "g-format",
    authorities: [
      { kind: "lrm", label: "LRM Ch 12" },
      { kind: "internal", label: "Final Label Review" },
    ],
  },
  {
    id: "FP-13",
    label: "Signal word present, ALL CAPS, correct point size",
    section: "front_panel",
    severity: "error",
    groupId: "g-format",
    authorities: [
      { kind: "cfr", label: "40 CFR 156 Subpart D" },
      { kind: "lrm", label: "LRM Ch 7" },
    ],
  },
  {
    id: "FP-14",
    label:
      "POISON + skull & crossbones on front panel next to DANGER, in RED, for Tox I (acute oral/dermal/inhalation) or >=4% methanol",
    section: "front_panel",
    severity: "error",
    groupId: "g-format",
    authorities: [
      { kind: "cfr", label: "40 CFR 156 Subpart D" },
      { kind: "internal", label: "Final Label Review" },
    ],
  },
  {
    id: "FP-15",
    label: "Spanish signal word & use statement for Tox Cat I or II",
    section: "front_panel",
    severity: "error",
    groupId: "g-format",
    authorities: [
      { kind: "cfr", label: "40 CFR 156 Subpart D" },
      { kind: "lrm", label: "LRM Ch 7" },
    ],
  },
  {
    id: "FP-16",
    label: "Keep Out of Reach of Children: correct point size, separate line from signal word",
    section: "front_panel",
    severity: "error",
    groupId: "g-format",
    authorities: [
      { kind: "cfr", label: "40 CFR 156 Subpart D" },
      { kind: "lrm", label: "LRM Ch 7" },
    ],
  },
  {
    id: "FP-17",
    label:
      "First Aid on front panel for Tox Cat I; else referral statement near signal word matching EPA",
    section: "front_panel",
    severity: "error",
    groupId: "g-format",
    authorities: [
      { kind: "internal", label: "EPA First Aid Placement Guidance" },
      { kind: "cfr", label: "40 CFR 156 Subpart D" },
    ],
  },
  {
    id: "FP-18",
    label: "First Aid includes hotline number at bottom",
    section: "front_panel",
    severity: "warning",
    groupId: "g-format",
    authorities: [{ kind: "lrm", label: "LRM Ch 7" }],
  },
  {
    id: "FP-19",
    label: "'Note to Physician' below First Aid (required for Tox Cat I)",
    section: "front_panel",
    severity: "error",
    groupId: "g-format",
    authorities: [{ kind: "lrm", label: "LRM Ch 7" }],
  },
  {
    id: "FP-20",
    label:
      "'Compare to' claim near active ingredient, smaller than signal word, not over-prominent",
    section: "front_panel",
    severity: "warning",
    groupId: "g-format",
    authorities: [
      { kind: "lrm", label: "LRM Ch 12" },
      { kind: "internal", label: "Me-too AI guidance" },
    ],
  },
  {
    id: "PR-01",
    label: "Signal word immediately below 'Hazards to Humans and Domestic Animals' heading",
    section: "precautionary_statements",
    severity: "warning",
    groupId: "g-format",
    authorities: [{ kind: "lrm", label: "LRM Ch 7" }],
  },
  {
    id: "PR-02",
    label: "Referral statement matches EPA approval if not on front panel",
    section: "precautionary_statements",
    severity: "error",
    groupId: "g-format",
    authorities: [{ kind: "lrm", label: "LRM Ch 7" }],
  },
  {
    id: "PR-03",
    label: "Environmental Hazards under Precautionary Statements",
    section: "precautionary_statements",
    severity: "error",
    groupId: "g-format",
    authorities: [
      { kind: "cfr", label: "40 CFR 156 Subpart E" },
      { kind: "lrm", label: "LRM Ch 8" },
    ],
  },
  {
    id: "PR-04",
    label: "Physical or Chemical Hazards (if applicable) under Precautionary Statements",
    section: "precautionary_statements",
    severity: "error",
    groupId: "g-format",
    authorities: [
      { kind: "cfr", label: "40 CFR 156.78" },
      { kind: "lrm", label: "LRM Ch 9" },
    ],
  },
  {
    id: "PR-05",
    label: "PPE, Engineering Controls, User Safety Recommendations under Precautionary Statements",
    section: "precautionary_statements",
    severity: "error",
    groupId: "g-format",
    authorities: [
      { kind: "lrm", label: "LRM Ch 7" },
      { kind: "lrm", label: "LRM Ch 10" },
    ],
  },
  {
    id: "DFU-01",
    label: "Required statements directly below heading (e.g. 'It is a violation of Federal law…')",
    section: "directions_for_use",
    severity: "error",
    groupId: "g-format",
    authorities: [
      { kind: "cfr", label: "40 CFR 156 Subpart A" },
      { kind: "lrm", label: "LRM Ch 11" },
    ],
  },
  {
    id: "DFU-02",
    label: "Ag-Use Requirements box and/or Non-Ag-Use Requirements box if applicable",
    section: "directions_for_use",
    severity: "error",
    groupId: "g-format",
    authorities: [
      { kind: "cfr", label: "40 CFR Part 156 Subpart K (WPS)" },
      { kind: "lrm", label: "LRM Ch 10" },
    ],
  },
  {
    id: "DFU-03",
    label: "Directions for Use match approved master (no added/changed uses)",
    section: "directions_for_use",
    severity: "error",
    groupId: "g-format",
    authorities: [{ kind: "lrm", label: "LRM Ch 11" }],
  },
  {
    id: "SD-01",
    label: "Storage & Disposal located at end of Directions for Use",
    section: "storage_disposal",
    severity: "warning",
    groupId: "g-format",
    authorities: [{ kind: "lrm", label: "LRM Ch 13" }],
  },
  {
    id: "SD-02",
    label: "S&D heading point size same as KOOROC",
    section: "storage_disposal",
    severity: "warning",
    groupId: "g-format",
    authorities: [{ kind: "lrm", label: "LRM Ch 13" }],
  },
  {
    id: "SD-03",
    label: "Storage & Disposal language in a box",
    section: "storage_disposal",
    severity: "warning",
    groupId: "g-format",
    authorities: [
      { kind: "cfr", label: "40 CFR 156 Subpart H" },
      { kind: "lrm", label: "LRM Ch 13" },
    ],
  },
  {
    id: "SD-04",
    label: "'Refillable' / 'Nonrefillable Container' identified if both provided",
    section: "storage_disposal",
    severity: "error",
    groupId: "g-format",
    authorities: [
      { kind: "cfr", label: "40 CFR 156.140" },
      { kind: "lrm", label: "LRM Ch 13" },
    ],
  },
  {
    id: "SD-05",
    label: "Multi-package: net contents matches container-handling; each size spelled out",
    section: "storage_disposal",
    severity: "error",
    groupId: "g-format",
    authorities: [{ kind: "lrm", label: "LRM Ch 13" }],
  },
  {
    id: "SD-06",
    label: "Batch/lot placeholder present (required for non-refillable containers)",
    section: "storage_disposal",
    severity: "warning",
    groupId: "g-format",
    authorities: [{ kind: "cfr", label: "40 CFR 156.140" }],
  },
  {
    id: "NC-01",
    label: "Net Contents/Weight present",
    severity: "error",
    groupId: "g-format",
    authorities: [{ kind: "lrm", label: "LRM Ch 17" }],
  },
  {
    id: "NC-02",
    label: "'Contents' for liquids / 'Weight' for solids",
    severity: "warning",
    groupId: "g-format",
    authorities: [{ kind: "lrm", label: "LRM Ch 17" }],
  },
  {
    id: "NC-03",
    label: "Largest unit of measure used (e.g. 1 lb 10 oz, not 26 oz)",
    severity: "warning",
    groupId: "g-format",
    authorities: [{ kind: "lrm", label: "LRM Ch 17" }],
  },
  {
    id: "NC-04",
    label: "US units provided (metric allowed only in addition)",
    severity: "warning",
    groupId: "g-format",
    authorities: [{ kind: "lrm", label: "LRM Ch 17" }],
  },
  {
    id: "NC-05",
    label: "Net contents not smaller than approval (matches approved 8570-1 sizes)",
    severity: "error",
    groupId: "g-format",
    authorities: [{ kind: "internal", label: "EPA Form 8570-1 Section III" }],
  },
  {
    id: "GEN-01",
    label: "No type smaller than 6 point",
    severity: "error",
    groupId: "g-format",
    authorities: [
      { kind: "cfr", label: "40 CFR 156 Subpart A" },
      { kind: "lrm", label: "LRM Ch 3" },
    ],
  },
  {
    id: "GEN-02",
    label: "All language clear, not obscured by pictures/graphics",
    severity: "error",
    groupId: "g-format",
    authorities: [
      { kind: "lrm", label: "LRM Ch 3" },
      { kind: "lrm", label: "LRM Ch 16" },
    ],
  },
  {
    id: "BL-01",
    label:
      "Required elements securely attached to container (base label): product name, active ingredient stmt, signal word, KOOROC, First Aid, Precautionary (Env + Phys/Chem), Storage & Disposal, registrant name/address, reg number, est number, referral statement",
    severity: "error",
    groupId: "g-format",
    authorities: [
      { kind: "cfr", label: "40 CFR 156 Subpart A" },
      { kind: "internal", label: "Final Label Review" },
    ],
  },
  {
    id: "CMP-01",
    label:
      "Correctly identify FPL components (front panel, base label, booklet, insert, tube, box/case)",
    severity: "error",
    groupId: "g-format",
    authorities: [{ kind: "lrm", label: "LRM Ch 4" }],
  },
  {
    id: "CMP-02",
    label: "Identify which master-label language is required per component",
    severity: "error",
    groupId: "g-format",
    authorities: [
      { kind: "lrm", label: "LRM Ch 4" },
      { kind: "internal", label: "Final Label Review" },
    ],
  },
  {
    id: "CMP-03",
    label: "Component aligns to approved master referral statement",
    severity: "error",
    groupId: "g-format",
    authorities: [{ kind: "lrm", label: "LRM Ch 7" }],
  },
  {
    id: "TXT-01",
    label: "Identify text discrepancies between Master Label and FPL (location + ML vs FPL text)",
    severity: "error",
    groupId: "g-text",
    authorities: [
      { kind: "lrm", label: "LRM Ch 3" },
      { kind: "internal", label: "Final Label Review" },
    ],
  },
  {
    id: "TXT-02",
    label: "Identify typographical errors (incl. typos also present on Master Label)",
    severity: "warning",
    groupId: "g-text",
    authorities: [{ kind: "lrm", label: "LRM Ch 3" }],
  },
  {
    id: "TXT-03",
    label: "Per-span classification: required vs optional vs note-to-EPA-reviewer",
    severity: "error",
    groupId: "g-text",
    authorities: [
      { kind: "lrm", label: "LRM Ch 3" },
      { kind: "lrm", label: "LRM Ch 11" },
    ],
  },
  {
    id: "GR-00",
    label:
      "Gate: if graphic already on Master Label it is acceptable (unless EPA error); low-quality->high-quality/color is an acceptable departure",
    severity: "info",
    groupId: "g-format",
    authorities: [
      { kind: "internal", label: "graphics_analysis_instructions" },
      { kind: "lrm", label: "LRM Ch 16" },
    ],
  },
  {
    id: "GR-01",
    label: "Tables in Master Label match tables in FPL",
    severity: "error",
    groupId: "g-format",
    authorities: [{ kind: "lrm", label: "LRM Ch 16" }],
  },
  {
    id: "GR-02",
    label: "Graphics on FPL match Master Label",
    severity: "error",
    groupId: "g-format",
    authorities: [{ kind: "lrm", label: "LRM Ch 16" }],
  },
  {
    id: "GR-03",
    label: "Graphic not false or misleading (composition/efficacy/value/endorsement/safety)",
    severity: "error",
    groupId: "g-format",
    authorities: [
      { kind: "cfr", label: "40 CFR 156.10(a)(5)" },
      { kind: "statute", label: "7 USC 136(q)" },
      { kind: "lrm", label: "LRM Ch 16" },
    ],
  },
  {
    id: "GR-04",
    label: "Logo not false or misleading",
    severity: "error",
    groupId: "g-format",
    authorities: [
      { kind: "cfr", label: "40 CFR 156.10(a)(5)" },
      { kind: "statute", label: "7 USC 136(q)" },
    ],
  },
  {
    id: "GR-05",
    label: "Pictured pest must be a pest claimed/controlled by the product",
    severity: "error",
    groupId: "g-format",
    authorities: [
      { kind: "internal", label: "graphics_analysis_instructions" },
      { kind: "cfr", label: "40 CFR 156.10(a)(5)" },
    ],
  },
  {
    id: "GR-06",
    label: "Pictured food/crop/flower must have directions for that site",
    severity: "error",
    groupId: "g-format",
    authorities: [{ kind: "internal", label: "graphics_analysis_instructions" }],
  },
  {
    id: "GR-07",
    label: "User/applicator images consistent with PPE requirements",
    severity: "error",
    groupId: "g-format",
    authorities: [{ kind: "internal", label: "graphics_analysis_instructions" }],
  },
  {
    id: "GR-08",
    label: "No images of children unless registered for children or pools",
    severity: "error",
    groupId: "g-format",
    authorities: [{ kind: "internal", label: "graphics_analysis_instructions" }],
  },
  {
    id: "GR-09",
    label: "No candy/food-lookalike imagery or containers",
    severity: "error",
    groupId: "g-format",
    authorities: [{ kind: "internal", label: "graphics_analysis_instructions" }],
  },
  {
    id: "GR-10",
    label: "No safety/non-toxicity symbols (caduceus, rod of Asclepius)",
    severity: "error",
    groupId: "g-format",
    authorities: [{ kind: "internal", label: "graphics_analysis_instructions" }],
  },
  {
    id: "GR-11",
    label: "No residential-use imagery when product limited to commercial/industrial",
    severity: "error",
    groupId: "g-format",
    authorities: [{ kind: "internal", label: "graphics_analysis_instructions" }],
  },
  {
    id: "GR-12",
    label: "No recycling/other symbol implying recyclability if false",
    severity: "warning",
    groupId: "g-format",
    authorities: [{ kind: "internal", label: "graphics_analysis_instructions" }],
  },
  {
    id: "GR-13",
    label: "No EPA/government endorsement logo",
    severity: "error",
    groupId: "g-format",
    authorities: [
      { kind: "cfr", label: "40 CFR 156.10(a)(5)(v)" },
      { kind: "internal", label: "graphics_analysis_instructions" },
    ],
  },
  {
    id: "GR-14",
    label: "Organic claims/logos (OMRI etc.) only if EPA-approved for organic production",
    severity: "error",
    groupId: "g-format",
    authorities: [{ kind: "internal", label: "graphics_analysis_instructions" }],
  },
  {
    id: "GR-15",
    label: "No efficacy-implying logos ('Gold Standard for Performance')",
    severity: "error",
    groupId: "g-format",
    authorities: [
      { kind: "cfr", label: "40 CFR 156.10(a)(5)(ii)" },
      { kind: "internal", label: "graphics_analysis_instructions" },
    ],
  },
  {
    id: "GR-PATH",
    label:
      "Route graphic addition: non-notification / notification / amendment per Ch16+PRN bucket",
    severity: "error",
    groupId: "g-format",
    authorities: [
      { kind: "prn", label: "PR Notice 98-10" },
      { kind: "internal", label: "graphics_analysis_instructions" },
      { kind: "lrm", label: "LRM Ch 16" },
    ],
  },
  {
    id: "VM-01",
    label:
      "Gate: compare the FPL against its CONTEMPORANEOUS governing master, not the latest; differences vs a non-contemporaneous (older/newer) master are approved version drift, not violations",
    severity: "info",
    groupId: "g-format",
    authorities: [
      { kind: "lrm", label: "LRM Ch 3" },
      { kind: "prn", label: "PR Notice 98-10" },
      { kind: "cfr", label: "40 CFR Part 156" },
    ],
  },
  {
    id: "REG-01",
    label: "Advise compliant revisions vs the current Master Label",
    severity: "error",
    groupId: "g-format",
    authorities: [
      { kind: "lrm", label: "LRM" },
      { kind: "cfr", label: "40 CFR Part 156" },
    ],
  },
  {
    id: "REG-02",
    label: "Classify change as non-notification / notification / amendment",
    severity: "error",
    groupId: "g-format",
    authorities: [{ kind: "prn", label: "PR Notice 98-10" }],
  },
  {
    id: "OTH-01",
    label: "Brand name appropriate (not false or misleading)",
    severity: "error",
    groupId: "g-format",
    authorities: [
      { kind: "cfr", label: "40 CFR 156.10(a)(5)" },
      { kind: "statute", label: "7 USC 136(q)" },
    ],
  },
  {
    id: "OTH-02",
    label: "Distributor label: components requiring change identified",
    severity: "error",
    groupId: "g-format",
    authorities: [
      { kind: "cfr", label: "40 CFR 152.132" },
      { kind: "prn", label: "PR Notice 98-10" },
    ],
  },
  {
    id: "OTH-03",
    label: "Changed distributor components are appropriate",
    severity: "error",
    groupId: "g-format",
    authorities: [{ kind: "cfr", label: "40 CFR 152.132" }],
  },
  {
    id: "OTH-04",
    label: "Off-labeled sections identified and compliant with Master Label",
    severity: "error",
    groupId: "g-format",
    authorities: [
      { kind: "lrm", label: "LRM Ch 11" },
      { kind: "prn", label: "PR Notice 98-10" },
    ],
  },
  {
    id: "OTH-05",
    label: "EPA Est. No. correct, approved, and final-est designation clear if multiple",
    severity: "error",
    groupId: "g-format",
    authorities: [
      { kind: "cfr", label: "40 CFR Part 167" },
      { kind: "internal", label: "EPA Form 8570-4 (CSF)" },
    ],
  },
  {
    id: "DEF-01",
    label: "Each finding references the divergence from Master Label text",
    severity: "error",
    groupId: "g-format",
    authorities: [{ kind: "lrm", label: "LRM" }],
  },
  {
    id: "DEF-02",
    label: "Each finding cites regulatory authority (40 CFR / LRM / PR Notice)",
    severity: "error",
    groupId: "g-format",
    authorities: [
      { kind: "cfr", label: "40 CFR Part 156" },
      { kind: "lrm", label: "LRM" },
      { kind: "prn", label: "PR Notice 98-10" },
    ],
  },
  {
    id: "DEF-03",
    label: "Reasoning provided: input -> rule -> conclusion (audit-ready)",
    severity: "error",
    groupId: "g-format",
    authorities: [{ kind: "lrm", label: "LRM" }],
  },
  {
    id: "ST-01",
    label: "California: compare against CA approved label (uses/pests/DFU/restrictions)",
    severity: "error",
    groupId: "g-format",
    authorities: [{ kind: "internal", label: "CA DPR" }],
  },
  {
    id: "ST-02",
    label: "New York: NY-specific language & Restricted Use status compliance",
    severity: "error",
    groupId: "g-format",
    authorities: [{ kind: "internal", label: "NY DEC" }],
  },
]
